'use client';

import RichTextHeadline from 'components/shared/RichTextHeadline/RichTextHeadline';
import Flowers from '../../public/icons/chime-pictogram-flower.svg';
import DollarSmiley from '../../public/icons/chime-pictogram-financial-progress.svg';
import { PrebuiltHeadlineText } from 'types/constants';
import { MARKS } from '@contentful/rich-text-types';
import { Options } from '@contentful/rich-text-react-renderer';
import { DocumentWithEmbeddedEntries } from 'libs/contentful/types';
import { renderSuperscript } from 'utils/superscriptUtils';
import { cn } from 'utils/styles';

interface Props {
  richTextHeadline?: DocumentWithEmbeddedEntries;
  prebuiltHeadline?: PrebuiltHeadlineText;
  italicColor: string;
}

const Trademark = () => {
  return <span className="sm:text-[36px] sm:align-super">&trade;</span>;
};

const MillionsTrustChimeHeadline = () => {
  return (
    <>
      Millions <br />
      <span className="min-[1440px]:pl-[215px]">
        of people <br />
      </span>
      <span className="inline-block min-[1440px]:pl-[525px] whitespace-nowrap">trust&nbsp;Chime.</span>
    </>
  );
};

const MakeMoneyGrowFastHeadline = () => {
  return (
    <>
      Make <br />
      <span className="min-[1440px]:pl-[215px]">
        your money <br />
      </span>
      <span className="min-[1440px]:pl-[525px]">
        gr
        <span className="inline-block pl-1 sm:pl-2">
          <Flowers className="inline w-auto sm:h-[5.5rem] h-9" />
        </span>
        w fast.
      </span>
    </>
  );
};

const MostLovedBankingAppHeadline = () => {
  return (
    <>
      The #1 <br />
      <span className="min-[1440px]:pl-[165px]">
        most loved <br />
      </span>
      <span className="min-[1440px]:pl-[370px]">
        banking app
        <Trademark />
      </span>
    </>
  );
};
const MaxRefundHeadline = () => {
  return (
    <>
      Your <span className="text-accent-green-400">max refund</span> <br />
      <span className="min-[1440px]:pl-[607px]">is waiting.</span>
    </>
  );
};
const UnlockFinancialProgressHeadline = () => {
  return (
    <>
      Unlock <br />
      <span className="min-[1440px]:pl-[215px]">
        Financial <br />
      </span>
      <span className="min-[1440px]:pl-[525px]">
        Pr
        <span className="inline">
          <DollarSmiley className="inline w-auto sm:h-[5.5rem] h-9" />
        </span>
        gress
        <Trademark />
      </span>
    </>
  );
};

const MakeTheSwitchTodayHeadline = () => {
  return (
    <>
      Make the <br />
      <span className="min-[1440px]:pl-[215px]">
        switch today.
        <br />
      </span>
    </>
  );
};

const SignUpForDollarsSenseHeadline = () => {
  return (
    <div className="md:text-9xl font-[670px] font-sans md:leading-[144px] md:tracking-[-0.72px]">
      Sign up for <br />
      <span className="min-[1440px]:pl-[215px]">
        Dollars & Sense
        <br />
      </span>
    </div>
  );
};

const YourMoneyShouldntCostMoneyHeadline = () => {
  return (
    <>
      Your money <br />
      shouldn&apos;t cost money.
      <Trademark />
    </>
  );
};

const getPreBuiltHeader = (text: string) => {
  switch (text) {
    case PrebuiltHeadlineText.MillionsTrustChime:
      return <MillionsTrustChimeHeadline />;
    case PrebuiltHeadlineText.MakeMoneyGrowFast:
      return <MakeMoneyGrowFastHeadline />;
    case PrebuiltHeadlineText.MostLovedBankingApp:
      return <MostLovedBankingAppHeadline />;
    case PrebuiltHeadlineText.MaxRefund:
      return <MaxRefundHeadline />;
    case PrebuiltHeadlineText.UnlockFinancialProgress:
      return <UnlockFinancialProgressHeadline />;
    case PrebuiltHeadlineText.MakeTheSwitchToday:
      return <MakeTheSwitchTodayHeadline />;
    case PrebuiltHeadlineText.SignUpForDollarsSense:
      return <SignUpForDollarsSenseHeadline />;
    case PrebuiltHeadlineText.YourMoneyShouldntCostMoney:
      return <YourMoneyShouldntCostMoneyHeadline />;
  }
};

export const Headline = ({ prebuiltHeadline, richTextHeadline, italicColor, ...rest }: Props) => {
  let header = <></>;

  if (richTextHeadline != null) {
    const customItalicsOpts: Options = {
      renderMark: {
        [MARKS.ITALIC]: text => <span className={italicColor}>{text}</span>,
        [MARKS.SUPERSCRIPT]: text => renderSuperscript(text),
      },
    };

    header = <RichTextHeadline headline={richTextHeadline} additionalOptions={customItalicsOpts} />;
  }

  if (prebuiltHeadline != null) {
    header = <h2>{getPreBuiltHeader(prebuiltHeadline)}</h2>;
  }

  return (
    <div
      className={cn(
        `font-sans text-heading-xxl-mobile md:text-heading-xxxxl font-semibold sm:tracking-[-2.88px]`,
        !prebuiltHeadline && 'sm:max-w-[960px]',
      )}
      {...rest}>
      {header}
    </div>
  );
};
